// The creative step of a published campaign.
//
// A creative change is never a campaign change (Gio, 2026-09-12): nothing here
// duplicates the campaign, mints a plan revision, or touches an ad set. What a
// live or paused campaign can do to its creatives is exactly four things —
// edit one (which mints a library revision the campaign does not fly until it
// is rolled out), roll a newer approved version out to one slot, add an
// approved creative as another slot, and pause or resume one slot (0030) —
// plus the two verbs an addition that did not finish needs: retry it, or
// cancel it and keep what is delivering. Every control that cannot act right
// now says why in words next to it, because a disabled button with no reason
// is the inert-control failure the wizard's Sections 4 and 5 already had once,
// and a reason that names a verb no control offers is the same failure with
// better manners.
//
// Each slot is a card (`CreativeSlotCard`): the creative's artwork and sizes,
// what its ads are doing at the provider (from `platform_ads` through the
// sync status, never from a journal), and what it has delivered (from the
// reporting query). Until 2026-09-18 a slot was its name and "Applied version
// N", and the add drawer was a `<select>` of names.

import { useEffect, useRef, useState } from 'react';
import { useCreative, useCreatives, useProductions } from '@/api/hooks';
import {
  useAddCampaignCreative,
  useCampaignCreativeAdditions,
  useCampaignCreativeMetrics,
  useCampaignPlan,
  useCampaignSlotPause,
  useCampaignSlotResume,
  useCampaignSyncStatus,
  useCancelCampaignCreativeAddition,
  useMetaCreativePreviews,
  useRetryCampaignCreativeAddition,
  useRolloutCreative,
} from '@/api/manage-hooks';
import type { Creative } from '@/api/types';
import type { ManageCampaignPlan } from '@/api/manage-types';
import type {
  CampaignCreativeAddition,
  CampaignCreativeSlotChange,
  CreativeRolloutStatus,
  MetaPreviewBundle,
} from '@/api/types.gen';
import { useCan } from '@/auth/capabilities';
import { campaignKindOf, campaignStatusLabel } from '@/lib/campaign-status';
import { creativeEditLock } from '@/lib/creative-lock';
import {
  creativePoster,
  effectiveSlotChange,
  latestForSlot,
  mediaLabel,
  metaSizeChips,
  posterInitials,
  slotChangeProgress,
  slotChangeTransitions,
  slotDelivery,
  slotMetricsByCreative,
} from '@/lib/creative-slot-view';
import { fmtDateInput } from '@/lib/datetime';
import { CreativeDetailDrawer, type CreativeDrawerTab } from '@/screens/library';
import { NewCreativeFlow } from '@/screens/new-creative';
import { ActionToast, type ActionToastModel } from './action-toast';
import { CreativeAddDrawer, type CreativeAddCandidate } from './creative-add-drawer';
import { CreativeSlotCard } from './creative-slot-card';
import { FailureNotice } from './failure-notice';
import { PinnedVersionBadge } from './pinned-version-badge';
import { ResponsiveOverlay } from './responsive-overlay';
import { ResponsivePicker } from './responsive-picker';
import { useCreativeAtRevision } from './use-creative-at-revision';

type Provider = 'meta' | 'ttd';

const providerNameOf = (provider: Provider) => (provider === 'ttd' ? 'The Trade Desk' : 'Meta');

type SlotCardProps = Parameters<typeof CreativeSlotCard>[0];

/**
 * One slot's card, drawn from the revision the slot flies.
 *
 * The library creative is the header and the mirror of its *latest* revision,
 * so once an edit has minted a draft its artwork and sizes are the draft's —
 * and a card labelled "Applied version 1" drew version 2's poster (audit,
 * 2026-09-18). The pinned revision's content comes from the archive; while it
 * loads the card shows its initials, never the wrong artwork. One component
 * per row, so the hook runs unconditionally.
 */
function SlotCardAtRevision({
  creative,
  revision,
  provider,
  ...rest
}: Omit<SlotCardProps, 'poster' | 'sizes' | 'isVideo' | 'mediaLabel' | 'formatLabel'> & {
  creative: Creative | undefined;
  revision: number;
  provider: Provider;
}) {
  const pinned = useCreativeAtRevision(creative, revision);
  return (
    <CreativeSlotCard
      {...rest}
      provider={provider}
      poster={creativePoster(pinned.creative)}
      isVideo={creative?.media_type === 'video'}
      mediaLabel={mediaLabel(creative)}
      formatLabel={creative?.format ?? ''}
      sizes={provider === 'meta' && pinned.creative ? metaSizeChips(pinned.creative) : undefined}
    />
  );
}

/**
 * What adding a creative does to delivery, said before and after the click.
 * On Meta the addition is a new ad in the existing ad set, so it delivers
 * only once Meta has reviewed it; on TTD the creative joins the ad group's
 * list and serves at once. Either way the existing ads keep delivering.
 */
export function additionDeliveryNote(provider: Provider): string {
  return provider === 'ttd'
    ? 'The creative joins the ad group and delivers immediately; existing ads keep delivering and nothing else changes.'
    : 'The new ad delivers once Meta has reviewed it, usually within 30–90 minutes; existing ads keep delivering and nothing else changes.';
}

/**
 * What pausing one creative does, on each provider. TTD has no ad object,
 * so there a pause detaches the creative from the ad groups and delivery
 * moves to the others at once; on Meta the slot's ads are paused and resume
 * without a new review.
 */
export function pauseDeliveryNote(provider: Provider, name: string, adSets: number): string {
  return provider === 'ttd'
    ? `The Trade Desk detaches ${name} from ${adSets === 1 ? 'the ad group' : `all ${adSets} ad groups`}. Delivery moves to the remaining creatives at once; resuming re-attaches it.`
    : `Meta pauses the ${adSets === 1 ? 'ad' : `${adSets} ads`} for ${name}${adSets === 1 ? '' : ', one in each ad set'}. Delivery on that creative stops within minutes; the other creatives keep delivering and nothing else in the campaign changes. Resuming turns the same ads back on, with no new review.`;
}

const inFlight = (status: string) => status === 'queued' || status === 'running';

/**
 * Where one addition has got to, in plain words. A journal status is a wire
 * value ('queued', 'running'), not a sentence, and this line is the only thing
 * standing between the user and silence while the queue works.
 */
/** The addition states the workspace narrates; `done` and `cancelled` are terminal. */
export type AdditionInFlight = 'queued' | 'running' | 'failed';

export function additionProgressLine(
  name: string,
  status: AdditionInFlight,
  provider: Provider,
): string {
  if (status === 'failed') {
    return `Adding ${name} did not finish at ${providerNameOf(provider)}. Nothing else in the campaign changed.`;
  }
  const phase = status === 'running' ? 'is being added' : 'is queued to be added';
  return `${name} ${phase} at ${providerNameOf(provider)} · not yet confirmed applied. ${additionDeliveryNote(provider)}`;
}

/** True when every Meta placement preview has rendered and been reviewed — the gate `add_creative` applies. */
export function metaPreviewsReviewed(bundle: MetaPreviewBundle | undefined): boolean {
  if (!bundle?.rendered || bundle.variants.length === 0) return false;
  return bundle.variants.every((variant) => bundle.reviewed_placements.includes(variant.placement));
}

/**
 * Why a finished campaign takes no new creative work. Its flights are over, so
 * an addition, a roll-out or a slot change would publish to a campaign that
 * delivers nothing more; the API refuses them too, in the same words
 * (`failure_copy::CAMPAIGN_FLIGHTS_OVER_CREATIVE_REFUSAL`). It points at no next step:
 * a creative change is never a campaign change (Gio, 2026-09-12). Cancelling an
 * addition stays open; retrying one is new work and is refused (2026-10-19).
 */
export const FINISHED_CAMPAIGN_CREATIVE_REASON =
  "This campaign's flights are over, so its creatives can't change.";

interface WorkspaceGate {
  canApply: boolean;
  eligible: boolean;
  /** The campaign's status in words ("ready to publish"), never the stored value. */
  status: string;
  /** Every flight of the current revision is over (`operational_status.phase`). */
  finished?: boolean;
  rolloutInFlight: number | null;
  unfinished: { name: string; status: AdditionInFlight } | undefined;
  additionsPending: boolean;
  additionsFailed: boolean;
  /** A pause or resume still in flight on some slot (0030). */
  changeInFlight?: { name: string; op: 'pause' | 'resume' };
}

/** Why a creative cannot be added, rolled out, paused or resumed right now, or null when it can. */
export function workspaceBlockedReason(
  verb: 'add' | 'roll out' | 'pause' | 'resume',
  gate: WorkspaceGate,
): string | null {
  const action = verb === 'add'
    ? 'Adding a creative'
    : verb === 'roll out'
      ? 'Rolling a version out'
      : verb === 'pause'
        ? 'Pausing a creative'
        : 'Resuming a creative';
  if (!gate.canApply) return `${action} needs the campaign launch permission.`;
  if (!gate.eligible) {
    return `Creative changes are available for live and paused campaigns; this one is ${gate.status}.`;
  }
  if (gate.finished) return FINISHED_CAMPAIGN_CREATIVE_REASON;
  if (gate.rolloutInFlight !== null) {
    return `A roll-out to version ${gate.rolloutInFlight} is in progress; ${verb} once it completes.`;
  }
  if (gate.unfinished) {
    // A journal status is never shown as a reason: 'running' is a wire value,
    // and 'failed' calls for a different action from the user than waiting.
    return gate.unfinished.status === 'failed'
      ? `Adding ${gate.unfinished.name} did not finish; retry it or cancel it before you ${verb} again.`
      : `${gate.unfinished.name} is still being added; ${verb} once it completes.`;
  }
  if (gate.changeInFlight) {
    return `${gate.changeInFlight.name} is being ${gate.changeInFlight.op === 'pause' ? 'paused' : 'resumed'}; ${verb} once it completes.`;
  }
  if (gate.additionsPending) return 'Checking for additions in progress…';
  if (gate.additionsFailed) return 'Publishing status could not be loaded; retry it below first.';
  return null;
}

/**
 * Why one slot's Pause or Resume is held, or null. The last creative still
 * delivering cannot be paused this way — that is what the campaign pause is
 * for — and the server refuses the same request with the same sentence.
 */
export function slotChangeBlockedReason(
  op: 'pause' | 'resume',
  gate: WorkspaceGate,
  slot: { paused: boolean; othersDelivering: number },
): string | null {
  const shared = workspaceBlockedReason(op, gate);
  if (shared) return shared;
  if (op === 'pause' && !slot.paused && slot.othersDelivering === 0) {
    return 'This is the only creative still delivering; pausing it would leave the campaign with nothing to serve. Pause the campaign instead.';
  }
  return null;
}

/** Why the picker's Apply is withheld, or null when the API would accept the request. */
export function applyBlockedReason(
  selected: Creative | undefined,
  provider: Provider,
  previews: { data: MetaPreviewBundle | undefined; pending: boolean },
): string | null {
  if (!selected) return 'Choose a creative to add.';
  const providerName = providerNameOf(provider);
  if (!selected.approved_revision) {
    return `${selected.name} has no approved version yet; review and approve it first.`;
  }
  // The library row describes the *current* revision. Only when that is the
  // approved one can its provider readiness be read from here; a newer draft
  // over it hides the approved revision's sync, and the API decides on apply.
  if (selected.approved_revision !== selected.revision) return null;
  const sync = selected.sync;
  if (!sync || sync.status !== 'synced' || !sync.external_id) {
    return `Version ${selected.revision} of ${selected.name} is still publishing to ${providerName}; apply once it has synced.`;
  }
  if (provider === 'meta') {
    if (previews.pending) return 'Checking the Meta placement previews…';
    if (!metaPreviewsReviewed(previews.data)) {
      return `Every Meta placement preview of version ${selected.revision} must be generated and reviewed before ${selected.name} can be added.`;
    }
  }
  return null;
}

/**
 * A prompt that replaces nothing on screen leaves a keyboard or screen-reader
 * user standing on a button that no longer does what it says, so land them on
 * the safe answer — the same move the content editor's discard prompt makes.
 */
function useSafeAnswerFocus(open: boolean) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);
  return ref;
}

function Details({ id, tab, close }: { id: string; tab: CreativeDrawerTab; close: () => void }) {
  const query = useCreative(id);
  const productions = useProductions();
  const [updated, setUpdated] = useState<Creative>();
  if (!query.data)
    return (
      <ResponsiveOverlay label="Creative details" onClose={close}>
        <p>{query.error ? 'Unable to load creative.' : 'Loading creative…'}</p>
        <button type="button" className="btn" onClick={() => void query.refetch()}>
          Retry
        </button>
      </ResponsiveOverlay>
    );
  const creative = updated ?? query.data;
  return (
    <CreativeDetailDrawer
      creative={creative}
      initialTab={tab}
      production={productions.data?.find((p) => p.id === creative.production_id) ?? null}
      onClose={close}
      onUpdated={setUpdated}
      onDeleted={close}
      onTabChange={() => {}}
    />
  );
}

export function CampaignCreativeWorkspace({ campaignId }: { campaignId: string }) {
  const query = useCampaignPlan(campaignId);
  const library = useCreatives();
  const additions = useCampaignCreativeAdditions(campaignId);
  const sync = useCampaignSyncStatus(campaignId, { enabled: !!campaignId });
  const add = useAddCampaignCreative();
  const retry = useRetryCampaignCreativeAddition();
  const cancel = useCancelCampaignCreativeAddition();
  const rollout = useRolloutCreative();
  const pause = useCampaignSlotPause();
  const resume = useCampaignSlotResume();
  const canEdit = useCan('creatives_edit');
  const canApply = useCan('campaigns_launch');
  const [details, setDetails] = useState<{ id: string; tab: CreativeDrawerTab }>();
  const [picker, setPicker] = useState(false);
  const [create, setCreate] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [discardCreate, setDiscardCreate] = useState(false);
  const [cancelAddition, setCancelAddition] = useState(false);
  const [confirmChange, setConfirmChange] = useState<{ position: number; op: 'pause' | 'resume' }>();
  // The request the user just confirmed, until the server answers; then the
  // row the server returned, until the plan carries it. Without these the
  // card sat unchanged for a whole re-read after the click (2026-09-18).
  const [pendingChange, setPendingChange] = useState<{ position: number; op: 'pause' | 'resume'; requestedAt: string }>();
  const [mutationRows, setMutationRows] = useState<Record<number, CampaignCreativeSlotChange>>({});
  const [toast, setToast] = useState<ActionToastModel | null>(null);
  // id → status of every slot change the last plan read carried, so a
  // completion or failure is announced once, on its transition.
  const seenChanges = useRef<Map<string, string> | null>(null);
  const keepEditing = useSafeAnswerFocus(discardCreate);
  const keepAddition = useSafeAnswerFocus(cancelAddition);
  const keepDelivering = useSafeAnswerFocus(!!confirmChange);
  const closeCreate = () => {
    if (createDirty) {
      setDiscardCreate(true);
      return false;
    }
    setCreate(false);
    return true;
  };
  const [choice, setChoice] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState('');
  const plan = query.data;
  const provider: Provider = plan?.campaign.channel === 'meta' ? 'meta' : 'ttd';
  const published = plan?.campaign.status === 'live' || plan?.campaign.status === 'paused';
  const todayStr = fmtDateInput(new Date());
  const metrics = useCampaignCreativeMetrics(
    campaignId,
    plan && published
      ? { from: plan.campaign.start_date, to: todayStr < plan.campaign.start_date ? plan.campaign.start_date : todayStr }
      : null,
  );
  useEffect(() => {
    const rows = plan?.creative_slot_changes ?? [];
    if (seenChanges.current) {
      const providerName = providerNameOf(plan?.campaign.channel === 'meta' ? 'meta' : 'ttd');
      for (const transition of slotChangeTransitions(seenChanges.current, rows)) {
        const name = library.data?.find((c) => c.id === transition.row.creative_id)?.name ?? 'The creative';
        const past = transition.row.op === 'pause' ? 'paused' : 'resumed';
        if (transition.to === 'done') {
          setToast({ key: `${transition.id}:done`, message: `${name} ${past} on ${providerName}.` });
        } else if (transition.to === 'failed') {
          setToast({
            key: `${transition.id}:failed`,
            message: `${name} could not be ${past} on ${providerName}: ${transition.row.failure?.headline ?? transition.row.last_error ?? 'the provider did not confirm the change.'}`,
          });
        }
      }
    }
    seenChanges.current = new Map(rows.map((row) => [row.id, row.status]));
  }, [plan?.creative_slot_changes, plan?.campaign.channel, library.data]);
  const candidates: CreativeAddCandidate[] = plan
    ? (library.data ?? [])
        .filter((c) =>
          c.production_id === plan.campaign.production_id &&
          c.channel === plan.campaign.channel &&
          c.status !== 'archived')
        .map((c) => ({
          creative: c,
          reason: plan.creatives.some((s) => s.creative_id === c.id)
            ? 'Already in this campaign'
            : c.approved_revision == null
              ? 'Approve it in the Creative Library first'
              : null,
        }))
        .sort((a, b) => Number(a.reason !== null) - Number(b.reason !== null))
    : [];
  const selected = candidates.find((row) => row.creative.id === choice && row.reason === null)?.creative;
  // The Meta gate the API applies is about the approved revision's previews;
  // they are only readable here while that revision is the current one.
  const previews = useMetaCreativePreviews(
    selected?.id ?? '',
    provider === 'meta' &&
      !!selected &&
      selected.approved_revision !== null &&
      selected.approved_revision === selected.revision,
  );
  if (!plan)
    return (
      <p role="status">
        {query.error ? 'Unable to load campaign creatives.' : 'Loading campaign creatives…'}
      </p>
    );
  const providerName = providerNameOf(provider);
  const eligible = plan.campaign.status === 'live' || plan.campaign.status === 'paused';
  // The words for what the campaign is, never its stored value: a user read
  // "this one is ready_to_publish" here.
  const statusWords = campaignStatusLabel(campaignKindOf(plan.campaign));
  const finished = plan.campaign.operational_status?.phase === 'finished';
  const nameOf = (creativeId: string) =>
    library.data?.find((c) => c.id === creativeId)?.name ?? 'this creative';
  // `done` and `cancelled` are both terminal: migration 0026's in-flight index
  // and the rollout guard count only `queued`/`running`, so a cancelled
  // addition blocks nothing and must not be reported as one in progress.
  const inFlightAddition = (status: CreativeRolloutStatus): status is AdditionInFlight =>
    status === 'queued' || status === 'running' || status === 'failed';
  const additionRow: CampaignCreativeAddition | undefined = additions.data?.find((a) =>
    inFlightAddition(a.status),
  );
  // The pin is written LAST — with the `done` verdict, in one batch, once every
  // provider write has landed — and the journal row's position is allocated
  // above every slot the plan carries. So an unfinished addition has no slot,
  // cannot be reached from the slot loop, and this block is the only place it
  // is on screen: its words and its two verbs both belong here.
  const unfinished =
    additionRow && inFlightAddition(additionRow.status)
      ? {
          id: additionRow.id,
          status: additionRow.status,
          name: nameOf(additionRow.creative_id),
          error: additionRow.last_error,
          failure: additionRow.failure ?? null,
        }
      : undefined;
  const rolloutInFlight = plan.creative_rollouts.find((r) => inFlight(r.status));
  // The row a card reads for its slot: the confirmed-but-unanswered request,
  // then the server's answer, then the plan — so the other verbs lock and the
  // card narrates the instant the user confirms, not a re-read later.
  const changeFor = (slot: ManageCampaignPlan['creatives'][number]) =>
    effectiveSlotChange({
      planRow: latestForSlot(plan.creative_slot_changes ?? [], slot.position),
      mutationRow: mutationRows[slot.position],
      pending: pendingChange?.position === slot.position
        ? {
            ...pendingChange,
            campaignId: plan.campaign.id,
            campaignRevision: plan.revision,
            creativeId: slot.creative_id,
            target: provider,
          }
        : null,
    });
  const changeInFlightRow = plan.creatives
    .map(changeFor)
    .find((c): c is CampaignCreativeSlotChange => !!c && inFlight(c.status));
  const gate: WorkspaceGate = {
    canApply,
    eligible,
    status: statusWords,
    finished,
    rolloutInFlight: rolloutInFlight ? rolloutInFlight.to_revision : null,
    unfinished: unfinished ? { name: unfinished.name, status: unfinished.status } : undefined,
    additionsPending: additions.isPending,
    additionsFailed: !!additions.error,
    changeInFlight: changeInFlightRow
      ? { name: nameOf(changeInFlightRow.creative_id), op: changeInFlightRow.op }
      : undefined,
  };
  const addReason = workspaceBlockedReason('add', gate);
  const rolloutReason = workspaceBlockedReason('roll out', gate);
  const applyReason = applyBlockedReason(selected, provider, {
    data: previews.data,
    pending: previews.isPending && previews.fetchStatus !== 'idle',
  });
  const addHintId = 'campaign-creative-add-why';
  const additionHintId = 'campaign-creative-addition-why';
  // Cancel is cleanup: it stays open on any campaign, a finished one included,
  // as the API takes it — it is what stops whatever a half-finished addition
  // left delivering. Retry runs the whole addition again, new ads included, so
  // it needs a live or paused campaign whose flights are not over, as the API
  // does (2026-10-19).
  const cancelReason = !canApply
    ? 'Retrying or cancelling an addition needs the campaign launch permission.'
    : null;
  const retryReason = cancelReason
    ?? (!eligible
      ? `Retrying an addition needs a live or paused campaign; this one is ${statusWords}.`
      : finished
        ? FINISHED_CAMPAIGN_CREATIVE_REASON
        : null);
  const metricsByCreative = slotMetricsByCreative(metrics.totals?.rows, metrics.daily?.rows);
  const reportedDays = Math.max(0, ...[...metricsByCreative.values()].map((m) => m.days));
  const groupsForProvider = (sync.data?.delivery_groups ?? []).filter((g) => g.target === provider).length
    || plan.audience_segments.length;
  const deliveringSlots = plan.creatives.filter((slot) => !slot.paused_at).length;
  const pending = pause.isPending || resume.isPending;
  async function applyAddition() {
    if (!selected?.approved_revision || !plan) return;
    setError('');
    try {
      await add.mutateAsync({
        id: campaignId,
        body: {
          creative_id: selected.id,
          creative_revision: selected.approved_revision,
          expected_campaign_revision: plan.revision,
          idempotency_key: requestId,
        },
      });
      setPicker(false);
      setChoice('');
      setRequestId(crypto.randomUUID());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to add creative.');
    }
  }
  const confirming = confirmChange
    ? plan.creatives.find((slot) => slot.position === confirmChange.position)
    : undefined;
  return (
    <div className="campaign-creative-workspace">
      <div className="creative-slot-intro">
        <p>
          {provider === 'meta'
            ? 'Each creative is one ad in every ad set. Pausing one keeps the others delivering; a newer approved version rolls out as a new ad; editing here mints a new version the campaign flies only once it is rolled out.'
            : 'Each creative is attached to every ad group. Pausing one detaches it and the others keep delivering; a newer approved version swaps in without a review; editing here mints a new version the campaign flies only once it is rolled out.'}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!!addReason}
          aria-describedby={addReason ? addHintId : undefined}
          onClick={() => {
            setPicker(true);
            setError('');
          }}
        >
          Add creative
        </button>
      </div>
      {addReason && (
        <p id={addHintId} className="field-hint">
          {addReason}
        </p>
      )}
      {/* An accepted addition has no slot until its pin lands, so the campaign
          says what is happening — and what it means for delivery — here, where
          the user who just clicked Apply is looking, and offers the only two
          verbs that exist for it. */}
      {unfinished && (
        <div className="panel campaign-creative-addition" style={{ padding: 16, marginBottom: 12 }}>
          <p role="status">{additionProgressLine(unfinished.name, unfinished.status, provider)}</p>
          {/* A requeued row carries the error of the attempt that failed while
              the queue is still working on it, so the sentence after it must
              not promise a verb that is not on screen. */}
          {unfinished.error && (
            <div role="alert">
              {/* The words, not the machine string; the string is under
                  "Technical details". Retry and Cancel stay below, as the
                  addition's own verbs. */}
              <FailureNotice announce={false} failure={unfinished.failure} fallback={unfinished.error} />
              <p>
                Some steps at {providerName} may have completed;{' '}
                {unfinished.status === 'failed'
                  ? 'retry resumes the addition where it stopped.'
                  : 'the addition is still being tried.'}
              </p>
            </div>
          )}
          {/* Retry is the failed row's verb; the server refuses it for anything
              else. Cancel is the only verb that unwedges a campaign whose
              addition will never succeed, and the server takes it for a failed
              or a queued row — never a running one, which still owns its
              execution lease. */}
          {unfinished.status === 'failed' && (
            <button
              type="button"
              className="btn"
              aria-label={`Retry addition of ${unfinished.name}`}
              disabled={!!retryReason || retry.isPending || cancel.isPending}
              aria-describedby={retryReason ? additionHintId : undefined}
              onClick={() => retry.mutate({ id: campaignId, operation: unfinished.id })}
            >
              Retry addition
            </button>
          )}
          {unfinished.status !== 'running' && (
            <button
              type="button"
              className="btn"
              aria-label={`Cancel addition of ${unfinished.name}`}
              disabled={!!cancelReason || retry.isPending || cancel.isPending}
              aria-describedby={cancelReason ? additionHintId : undefined}
              onClick={() => setCancelAddition(true)}
            >
              Cancel addition
            </button>
          )}
          {/* The reason under the verbs on screen: Retry shows only for a failed row. */}
          {(cancelReason ?? (unfinished.status === 'failed' ? retryReason : null)) && (
            <p id={additionHintId} className="field-hint">
              {cancelReason ?? retryReason}
            </p>
          )}
          {cancelAddition && unfinished.status !== 'running' && (
            <div
              className="panel"
              role="alertdialog"
              aria-label={`Cancel adding ${unfinished.name}`}
            >
              {/* True whether or not anything was created at the provider: the
                  campaign's own creatives are untouched either way, because
                  the addition never pinned a slot. */}
              <p>
                {plan.campaign.name} keeps the creatives it is delivering now; {unfinished.name} is
                not added, and nothing else in the campaign changes.
              </p>
              <p>
                The half-finished addition is abandoned: it is never retried, and anything{' '}
                {providerName} already created for it stops being part of this campaign. To add{' '}
                {unfinished.name} later, add it again from the start.
              </p>
              <button
                ref={keepAddition}
                type="button"
                className="btn"
                onClick={() => setCancelAddition(false)}
              >
                Not now
              </button>
              <button
                type="button"
                className="btn"
                disabled={cancel.isPending}
                onClick={() => {
                  setCancelAddition(false);
                  cancel.mutate({ id: campaignId, operation: unfinished.id });
                }}
              >
                Cancel the addition
              </button>
            </div>
          )}
        </div>
      )}
      {additions.error && (
        <p role="alert">
          Unable to load publishing status.{' '}
          <button type="button" className="btn" onClick={() => void additions.refetch()}>
            Retry
          </button>
        </p>
      )}
      {sync.error && !sync.data && (
        <p className="field-hint" role="status">Delivery status could not be read; the cards show what the plan knows.</p>
      )}
      {metrics.error && (
        /* A failed reporting read is said, not rendered as zeros: a card
           reading "0 impressions" beside a live ad is a claim. */
        <p className="field-hint" role="status">
          Metrics unavailable; the cards show delivery without them.{' '}
          <button type="button" className="btn btn-sm" onClick={() => metrics.refetch()}>
            Retry
          </button>
        </p>
      )}
      <div className={`creative-slot-list-head${provider === 'ttd' ? ' creative-slot-list-head--wide' : ''}`} aria-hidden="true">
        <span className="creative-slot-eyebrow">Creative</span>
        <span />
        <span className="creative-slot-eyebrow">Delivery</span>
        <span className="creative-slot-eyebrow">{reportedDays > 0 ? `Last ${reportedDays} day${reportedDays === 1 ? '' : 's'}` : 'Delivered'}</span>
      </div>
      <div className="creative-slot-list">
        {plan.creatives.map((slot) => {
          const creative = library.data?.find((c) => c.id === slot.creative_id);
          const name = creative?.name ?? 'this creative';
          const latest = latestForSlot(plan.creative_rollouts, slot.position);
          const change = changeFor(slot);
          const delivery = slotDelivery({ provider, slot, sync: sync.data, rollout: latest, change });
          const paused = !!slot.paused_at;
          // The stepper shows while the change runs, and for a failed change
          // the slot does not yet reflect — the same test the delivery model
          // applies before it reports the failure.
          const progress = change && (inFlight(change.status) || (change.status === 'failed' && (change.op === 'pause') !== paused))
            ? slotChangeProgress({ change, stages: sync.data?.stages ?? [], provider, audiences: groupsForProvider })
            : null;
          const editReason = !canEdit
            ? 'Editing a creative needs the creatives permission.'
            : !creative
              ? 'This creative is not in your library view, so it cannot be edited from here.'
              : (creativeEditLock(creative)?.message ?? null);
          const op: 'pause' | 'resume' = paused ? 'resume' : 'pause';
          const changeReason = delivery.busy
            ? 'Waiting for the current change on this creative to finish.'
            : slotChangeBlockedReason(op, gate, {
                paused,
                othersDelivering: deliveringSlots - (paused ? 0 : 1),
              });
          // Stable, not `useId()`: these ids are read by aria-describedby and by
          // the phone suite's locators, and a generated ':r0:' is not a valid
          // CSS id selector. One workspace renders per campaign screen.
          const slotHint = `campaign-creative-slot-${slot.position}`;
          const newer = creative && creative.approved_revision !== null && creative.approved_revision > slot.creative_revision
            ? creative.approved_revision
            : null;
          return (
            <SlotCardAtRevision
              key={slot.position}
              id={`${slotHint}-card`}
              ariaLabel={`Creative slot ${slot.position + 1}: ${name}`}
              name={name}
              creative={creative}
              revision={slot.creative_revision}
              initials={posterInitials(name)}
              versionLabel={`Applied version ${slot.creative_revision}`}
              newerVersion={newer}
              provider={provider}
              delivery={delivery}
              progress={progress}
              metrics={metrics.error
                ? null
                : (metricsByCreative.get(slot.creative_id) ?? (metrics.totals ? { impressions: 0, clicks: 0, spendCents: 0, videoStarts: 0, videoCompletions: 0, days: 0, series: [] } : null))}
              actions={
                <>
                  <button
                    type="button"
                    className={`btn btn-sm${paused ? ' btn-primary' : ''}`}
                    aria-label={`${paused ? 'Resume' : 'Pause'} creative ${name}`}
                    disabled={!!changeReason || pending}
                    aria-describedby={changeReason ? `${slotHint}-change` : undefined}
                    data-lock-exempt
                    onClick={() => setConfirmChange({ position: slot.position, op })}
                  >
                    {paused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label={`Preview creative ${name}`}
                    disabled={!creative}
                    onClick={() => setDetails({ id: slot.creative_id, tab: 'details' })}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    aria-label={`Edit creative ${name}`}
                    disabled={!!editReason}
                    aria-describedby={editReason ? `${slotHint}-edit` : undefined}
                    onClick={() => setDetails({ id: slot.creative_id, tab: 'details' })}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    aria-label={`Versions of ${name}`}
                    disabled={!creative}
                    onClick={() => setDetails({ id: slot.creative_id, tab: 'versions' })}
                  >
                    Versions
                  </button>
                  {changeReason && (
                    <span id={`${slotHint}-change`} className="field-hint" style={{ flexBasis: '100%' }}>
                      {changeReason}
                    </span>
                  )}
                  {editReason && (
                    <span id={`${slotHint}-edit`} className="field-hint" style={{ flexBasis: '100%' }}>
                      {editReason}
                    </span>
                  )}
                </>
              }
              footer={newer === null && !(latest && latest.status !== 'done') ? undefined : (
                <PinnedVersionBadge
                  creative={creative}
                  pinnedRevision={slot.creative_revision}
                  campaignLive
                  campaignName={plan.campaign.name}
                  provider={provider}
                  rollout={latest}
                  rolloutPending={rollout.isPending}
                  rolloutUnavailableReason={rolloutReason ?? undefined}
                  onVersions={() => setDetails({ id: slot.creative_id, tab: 'versions' })}
                  onRollout={
                    rolloutReason === null
                      ? (toRevision) =>
                          rollout.mutate({
                            campaignId,
                            position: slot.position,
                            creative_revision: toRevision,
                            expected_creative_revision: slot.creative_revision,
                          })
                      : undefined
                  }
                />
              )}
            />
          );
        })}
      </div>
      {(rollout.error || retry.error || cancel.error || pause.error || resume.error) && (
        <p role="alert">{(rollout.error ?? retry.error ?? cancel.error ?? pause.error ?? resume.error)?.message}</p>
      )}
      {confirmChange && confirming && (
        <ResponsivePicker
          kind="confirmation"
          label={`${confirmChange.op === 'pause' ? 'Pause' : 'Resume'} creative`}
          onClose={() => setConfirmChange(undefined)}
        >
          <div className="panel pinned-version-confirm" role="alertdialog" aria-label={`${confirmChange.op === 'pause' ? 'Pause' : 'Resume'} ${nameOf(confirming.creative_id)}`} data-slot-change-confirm>
            <p>
              {confirmChange.op === 'pause'
                ? pauseDeliveryNote(provider, nameOf(confirming.creative_id), groupsForProvider)
                : provider === 'ttd'
                  ? `The Trade Desk re-attaches ${nameOf(confirming.creative_id)} to ${groupsForProvider === 1 ? 'the ad group' : `all ${groupsForProvider} ad groups`}; it serves again as soon as the swap lands.`
                  : `Meta turns the ${groupsForProvider === 1 ? 'ad' : `${groupsForProvider} ads`} for ${nameOf(confirming.creative_id)} back on. They take the status of the ad set they are in, so a paused campaign or a dark flight keeps them paused until it resumes.`}
            </p>
            <div className="pinned-version-confirm-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={pending}
                onClick={() => {
                  const request = {
                    campaignId,
                    position: confirming.position,
                    expected_creative_revision: confirming.creative_revision,
                  };
                  const op = confirmChange.op;
                  const position = confirming.position;
                  const name = nameOf(confirming.creative_id);
                  setConfirmChange(undefined);
                  // Said and shown the instant the user confirms: the card
                  // goes busy on a stub row and the toast names what is
                  // happening. The server's row replaces the stub when it
                  // answers; the plan's row replaces that when it is read.
                  setPendingChange({ position, op, requestedAt: new Date().toISOString() });
                  setToast({
                    key: `${op}-${position}-${Date.now()}`,
                    message: `${op === 'pause' ? 'Pausing' : 'Resuming'} ${name} on ${providerName} — usually under a minute.`,
                  });
                  void (op === 'pause' ? pause : resume)
                    .mutateAsync(request)
                    .then((row) => setMutationRows((rows) => ({ ...rows, [position]: row })))
                    .catch(() => undefined)
                    .finally(() => setPendingChange((current) => (current?.position === position ? undefined : current)));
                }}
              >
                {confirmChange.op === 'pause' ? 'Pause creative' : 'Resume creative'}
              </button>
              <button ref={keepDelivering} type="button" className="btn btn-ghost" onClick={() => setConfirmChange(undefined)}>
                {confirmChange.op === 'pause' ? 'Keep delivering' : 'Keep paused'}
              </button>
            </div>
          </div>
        </ResponsivePicker>
      )}
      {picker && (
        <CreativeAddDrawer
          campaignName={plan.campaign.name}
          provider={provider}
          candidates={candidates}
          selectedId={choice}
          onSelect={(id) => {
            setChoice(id);
            setRequestId(crypto.randomUUID());
          }}
          onCreateNew={canEdit
            ? () => {
                setCreateDirty(false);
                setCreate(true);
              }
            : undefined}
          onReviewSelected={() => {
            if (selected) setDetails({ id: selected.id, tab: 'details' });
          }}
          previews={previews.data}
          applyReason={applyReason}
          readyLine={selected && selected.approved_revision !== selected.revision
            ? `Version ${selected.approved_revision} of ${selected.name} is the one added; the newer draft (version ${selected.revision}) stays in the library, and its readiness at ${providerName} is checked on apply.`
            : `Version ${selected?.approved_revision} of ${selected?.name} is approved, synced and ready to add.`}
          deliveryNote={additionDeliveryNote(provider)}
          applying={add.isPending}
          error={error || undefined}
          onApply={() => void applyAddition()}
          onClose={() => setPicker(false)}
        />
      )}
      {create && (
        <ResponsiveOverlay
          label="Create campaign creative"
          className="creative-create-overlay"
          kind="form"
          onClose={closeCreate}
        >
          {/* Rendered inside the create overlay rather than as a sibling: a
              second ResponsiveOverlay would push a second touch-layout
              history entry over the first, and Back would then close the
              question instead of returning to the form it asks about. */}
          {discardCreate && (
            <div className="panel" role="alertdialog" aria-label="Discard creative draft changes">
              <p>Discard unsaved changes? Any saved draft remains in the Creative Library.</p>
              <button
                ref={keepEditing}
                type="button"
                className="btn"
                onClick={() => setDiscardCreate(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setDiscardCreate(false);
                  setCreate(false);
                }}
              >
                Discard changes
              </button>
            </div>
          )}
          <NewCreativeFlow
            initialProductionId={plan.campaign.production_id}
            initialChannel={plan.campaign.channel}
            heading={`New creative for ${plan.campaign.name}`}
            subtitle={`Saved to the Creative Library · added to ${plan.campaign.name} only when you apply it`}
            setView={closeCreate}
            onDirtyChange={setCreateDirty}
            onSaved={(id: string) => {
              setCreate(false);
              setChoice(id);
              setRequestId(crypto.randomUUID());
              setDetails({ id, tab: 'details' });
            }}
          />
        </ResponsiveOverlay>
      )}
      {details && (
        <Details
          key={details.id}
          id={details.id}
          tab={details.tab}
          close={() => setDetails(undefined)}
        />
      )}
      <ActionToast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
